import { useState } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { deleteOrganization, updateOrganization } from '../lib/api'
import { useConfirm } from '../components/ConfirmDialog'
import type { TenantDetailContext } from './TenantDetailLayout'
import { normalizeImageKitUrl } from '@lic/media-urls'

export default function TenantOverviewPage() {
  const { org, reload } = useOutletContext<TenantDetailContext>()
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  const navigate = useNavigate()
  const { confirm, dialog } = useConfirm()

  const [toggling, setToggling] = useState(false)

  async function handleToggleBlast() {
    if (!token || !id || toggling) return
    setToggling(true)
    try {
      await updateOrganization(token, id, { blast_email_enabled: !org.blast_email_enabled })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal mengubah pengaturan.')
    } finally {
      setToggling(false)
    }
  }

  async function handleDelete() {
    if (!token || !id) return
    const ok = await confirm(
      `"${org.name}" dan semua akun organizer-nya akan dihapus. Event yang sudah ada tidak ikut terhapus.`,
      { title: `Hapus org "${org.name}"?`, confirmLabel: 'Hapus' }
    )
    if (!ok) return
    try {
      await deleteOrganization(token, id)
      navigate('/sales/tenants')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal menghapus org.')
    }
  }

  const created = new Date(org.created_at).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div className="p-6 max-w-2xl">
      {dialog}

      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800">
          <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Informasi Org</h2>
        </div>
        <div className="p-5 space-y-4">
          {org.logo_url && (
            <div>
              <p className="text-xs text-zinc-500 mb-2">Logo</p>
              <img src={normalizeImageKitUrl(org.logo_url)} alt={org.name} className="h-12 rounded object-contain" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-zinc-500">Nama</p>
              <p className="text-sm text-white mt-1">{org.name}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Slug</p>
              <p className="text-sm text-white font-mono mt-1">{org.slug}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Dibuat</p>
            <p className="text-sm text-white mt-1">{created}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800">
          <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Fitur</h2>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Blast Email</p>
              <p className="text-xs text-zinc-500 mt-0.5">Izinkan org mengirim blast email ke buyer</p>
            </div>
            <button
              onClick={handleToggleBlast}
              disabled={toggling}
              aria-label="Toggle blast email"
              className="relative shrink-0 focus:outline-none"
            >
              <div className={`w-9 h-5 rounded-full transition-colors ${org.blast_email_enabled ? 'bg-yellow-500' : 'bg-zinc-700'} ${toggling ? 'opacity-50' : ''}`} />
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${org.blast_email_enabled ? 'translate-x-4' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-zinc-800">
        <p className="text-xs text-zinc-500 mb-3">Danger Zone</p>
        <button
          onClick={handleDelete}
          className="px-4 py-2 text-sm text-red-400 border border-red-900 rounded-lg hover:bg-red-950 transition-colors"
        >
          Hapus Org
        </button>
        <p className="text-xs text-zinc-600 mt-2">Tidak bisa dihapus jika org masih punya sale events.</p>
      </div>
    </div>
  )
}
